import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Preview için tipimizi tanımlayalım
interface PreviewState {
  title: string
  description: string
  profilePhoto: string | null
  iconShape: number
  iconPlacement: number
  themeButtonBackgroundColor: string
  themeButtonColor: string
  themeButtonFont: string
  themeBackgroundColor: string
  themeForegroundColor: string
  url: string
  page: any
  links: any[]
  profileIcons: any[]
  normalLinks: any[]
  headerLinks: any[]
  formLinks: any[]
  orderedLinks: any[]
  galleryImages: any[]
  galleries: { [key: string]: any[] }
  galleryNames: string[]
  videos: any[]
  isPreviewOnly: boolean
  pageLayout: string[]
  customIcons: any[] // Özel ikonlar için yeni alan ekledik
}

// Başlangıç değerlerini tanımlayalım
const initialState: PreviewState = {
  title: 'İsimsiz Sayfa',
  description: '',
  profilePhoto: null,
  iconShape: 0,
  iconPlacement: 2,
  themeButtonBackgroundColor: '#4C4C9A',
  themeButtonColor: 'white',
  themeButtonFont: 'inherit',
  themeBackgroundColor: '#f5f5f5',
  themeForegroundColor: '#333333',
  url: '',
  page: {},
  links: [],
  profileIcons: [],
  normalLinks: [],
  headerLinks: [],
  formLinks: [],
  orderedLinks: [],
  galleryImages: [],
  galleries: {},
  galleryNames: [],
  videos: [],
  isPreviewOnly: false,
  pageLayout: ['profile_image', 'title', 'about', 'icons', 'links'],
  customIcons: [] // Özel ikonlar için boş başlangıç dizisi
}

// Preview slice'ını oluşturalım
const previewSlice = createSlice({
  name: 'preview',
  initialState,
  reducers: {
    // Temel bilgileri güncelleme
    updateBasicInfo: (
      state,
      action: PayloadAction<{
        title?: string
        description?: string
        profilePhoto?: string | null
        iconShape?: number
        iconPlacement?: number
        isPreviewOnly?: boolean
      }>
    ) => {
      if (action.payload.title !== undefined) {
        state.title = action.payload.title
      }
      if (action.payload.description !== undefined) {
        state.description = action.payload.description
      }
      if (action.payload.profilePhoto !== undefined) {
        state.profilePhoto = action.payload.profilePhoto
      }
      if (action.payload.iconShape !== undefined) {
        state.iconShape = action.payload.iconShape
      }
      if (action.payload.iconPlacement !== undefined) {
        state.iconPlacement = action.payload.iconPlacement
      }
      if (action.payload.isPreviewOnly !== undefined) {
        state.isPreviewOnly = action.payload.isPreviewOnly
      }
    },

    // İkon ayarlarını güncelleme
    updateIconSettings: (
      state,
      action: PayloadAction<{
        iconShape?: number
        iconPlacement?: number
      }>
    ) => {
      if (action.payload.iconShape !== undefined) {
        state.iconShape = action.payload.iconShape
      }
      if (action.payload.iconPlacement !== undefined) {
        state.iconPlacement = action.payload.iconPlacement
      }
    },

    // Tema renklerini güncelleme
    updateThemeColors: (
      state,
      action: PayloadAction<{
        themeButtonBackgroundColor?: string
        themeButtonColor?: string
        themeButtonFont?: string
        themeBackgroundColor?: string
        themeForegroundColor?: string
      }>
    ) => {
      if (action.payload.themeButtonBackgroundColor !== undefined) {
        state.themeButtonBackgroundColor = action.payload.themeButtonBackgroundColor
      }
      if (action.payload.themeButtonColor !== undefined) {
        state.themeButtonColor = action.payload.themeButtonColor
      }
      if (action.payload.themeButtonFont !== undefined) {
        state.themeButtonFont = action.payload.themeButtonFont
      }
      if (action.payload.themeBackgroundColor !== undefined) {
        state.themeBackgroundColor = action.payload.themeBackgroundColor
      }
      if (action.payload.themeForegroundColor !== undefined) {
        state.themeForegroundColor = action.payload.themeForegroundColor
      }
    },

    // Sayfa URL'ini güncelleme
    updateUrl: (state, action: PayloadAction<string>) => {
      state.url = action.payload
    },

    // Sayfa objesi güncelleme
    updatePage: (state, action: PayloadAction<any>) => {
      state.page = action.payload
    },

    // Page layout güncelleme
    updatePageLayout: (state, action: PayloadAction<string[]>) => {
      state.pageLayout = action.payload
    },

    // Linkleri güncelleme
    updateLinks: (state, action: PayloadAction<any[]>) => {
      state.links = action.payload

      // Farklı link türlerini filtrele
      const profileIcons = action.payload.filter(link => link.page_link_type === 5 && link.is_active)
      const normalLinks = action.payload.filter(link => link.page_link_type === 3 && link.is_active)
      const headerLinks = action.payload.filter(link => link.page_link_type === 1 && link.is_active)
      const formLinks = action.payload.filter(link => link.page_link_type === 2 && link.is_active)
      const customIconLinks = action.payload.filter(link => link.page_link_type === 6 && link.is_active)
      
      // Debug için log ekle
      console.log('📋 Redux updateLinks - Profil İkonları:', {
        totalLinks: action.payload.length,
        profileIconsCount: profileIcons.length,
        activeProfileIcons: profileIcons.filter(icon => icon.is_active).length
      })
      
      state.profileIcons = profileIcons
      state.normalLinks = normalLinks
      state.headerLinks = headerLinks
      state.formLinks = formLinks
      
      // Varsayılan sıralama - özel ikon linklerini de dahil et
      state.orderedLinks = [...headerLinks, ...formLinks, ...normalLinks, ...customIconLinks]
    },

    // Sıralanmış linkleri güncelleme
    updateOrderedLinks: (state, action: PayloadAction<any[]>) => {
      state.orderedLinks = action.payload
    },

    // Galeri resimlerini güncelleme
    updateGalleryImages: (state, action: PayloadAction<any[]>) => {
      state.galleryImages = action.payload
    },

    // Galeri yapısını güncelleme
    updateGalleries: (
      state,
      action: PayloadAction<{
        galleries: { [key: string]: any[] }
        galleryNames: string[]
      }>
    ) => {
      state.galleries = action.payload.galleries
      state.galleryNames = action.payload.galleryNames
    },

    // Videoları güncelleme
    updateVideos: (state, action: PayloadAction<any[]>) => {
      state.videos = action.payload
    },
    
    // Özel ikonları güncelleme
    updateCustomIcons: (state, action: PayloadAction<any[]>) => {
      state.customIcons = action.payload
      console.log('🔄 Redux - customIcons güncellendi:', action.payload.length);
    },

    // Tüm state'i güncelleme (örneğin ilk yüklemede)
    updateFullState: (state, action: PayloadAction<Partial<PreviewState>>) => {
      return { ...state, ...action.payload }
    },

    // State'i sıfırlama
    resetState: (state) => {
      return initialState
    }
  }
})

// Action'ları dışa aktaralım
export const {
  updateBasicInfo,
  updateIconSettings,
  updateThemeColors,
  updateUrl,
  updatePage,
  updatePageLayout,
  updateLinks,
  updateOrderedLinks,
  updateGalleryImages,
  updateGalleries,
  updateVideos,
  updateCustomIcons, // Yeni eklediğimiz eylem
  updateFullState,
  resetState
} = previewSlice.actions

// Reducer'ı dışa aktaralım
export default previewSlice.reducer 